export const REVIEW_STATUSES = ['passed', 'failed', 'held', 'unprocessed', 'processing'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function reviewPath(sourceId, attemptId) {
  if (!UUID.test(sourceId || '') || (attemptId != null && !UUID.test(attemptId))) return null;
  return `/review/${sourceId}${attemptId ? `/${attemptId}` : ''}`;
}

export function reviewStatus(status) {
  return {
    passed: { label: 'Rule line: Passed', tone: 'good' },
    failed: { label: 'Rule line: Failed', tone: 'bad' },
    held: { label: 'Rule line: Held', tone: 'warn' },
    unprocessed: { label: 'Rule line: Not run yet', tone: 'neutral' },
    processing: { label: 'Rule line: Processing', tone: 'warn' },
  }[status] || { label: 'Rule-line evidence unavailable', tone: 'warn' };
}

export function safeSourceUrl(url) {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch { return null; }
}

export function sourceJurisdiction(source) {
  const candidates = [
    source?.jurisdiction_code,
    source?.source_metadata?.jurisdiction,
    source?.source_metadata?.jurisdiction_code,
    source?.source_metadata?.registered_metadata?.jurisdiction,
    source?.source_metadata?.registered_metadata?.jurisdiction_code,
  ];
  const value = candidates.find((candidate) => String(candidate || '').trim());
  if (value) return String(value).trim();

  // Rosetta's selected-runtime detail may preserve the LegiScan provider URL
  // inside registered_metadata rather than emitting a top-level jurisdiction.
  // Only a genuine LegiScan hostname and first path state segment are accepted.
  const urls = [
    source?.source_metadata?.registered_metadata?.provider_url,
    source?.source_metadata?.docket_provider_url,
    source?.source_metadata?.registered_metadata?.docket_provider_url,
    source?.source_metadata?.provider_url,
    source?.source_url,
  ];
  for (const value of urls) {
    if (!value) continue;
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || !['legiscan.com', 'www.legiscan.com'].includes(url.hostname.toLowerCase())) continue;
      const state = url.pathname.match(/^\/([A-Za-z]{2})(?:\/|$)/)?.[1];
      if (state) return state.toUpperCase();
    } catch { /* Untrusted or malformed provider URL: no inference. */ }
  }
  return null;
}

export function assertReviewDetail(payload, sourceId, attemptId) {
  if (
    payload?.publication_status !== 'candidate' ||
    payload.scope !== 'rule_line_v9' ||
    payload.current_campaign_id !== null ||
    payload.source?.source_registry_id !== sourceId
  ) {
    throw new Error('The returned result does not match this current rule-line source.');
  }
  if (attemptId) throw new Error('Historical attempt links are not part of the current rule-line surface.');

  const source = payload.source;
  const selected = payload.selected_attempt;
  if (!selected || (
    selected.attempt_id != null ||
    selected.extraction_run_id != null ||
    selected.source_registry_id !== sourceId ||
    selected.source_document_id !== source.source_document_id ||
    selected.source_content_hash !== source.source_content_hash ||
    selected.engine_version !== 'rosetta-rule-ref-v9' ||
    selected.rule_set_version !== 'rosetta-rule-ref-v9'
  )) {
    throw new Error('The stored rule-line receipt does not match this source.');
  }

  if (payload.law_view && (
    payload.status !== 'passed' ||
    payload.law_view.extraction_run_id !== null ||
    payload.law_view.source_document_id !== source.source_document_id ||
    payload.law_view.source_content_hash !== source.source_content_hash ||
    payload.law_view.engine_version !== 'rosetta-rule-ref-v9' ||
    payload.law_view.rule_set_version !== 'rosetta-rule-ref-v9' ||
    !Array.isArray(payload.law_view.objects)
  )) {
    throw new Error('The stored rule-line output does not match this source.');
  }

  if (payload.status === 'unprocessed' && payload.law_view) {
    throw new Error('Not-run rule-line results cannot contain decomposition output.');
  }
  return payload;
}

export function feedbackReference(payload, note = '', origin = '') {
  const source = payload.source;
  const attempt = payload.selected_attempt;
  const path = reviewPath(source.source_registry_id, attempt?.attempt_id);
  return [
    'Rosetta rule-line v9 — feedback',
    `Law: ${source.document_name || source.document_identifier || source.source_registry_id}`,
    `Result: ${reviewStatus(payload.status || attempt?.status || source.status).label}`,
    `Rule line: ${attempt?.engine_version || 'rosetta-rule-ref-v9'}`,
    `Source revision: ${source.source_registry_id}`,
    `Source hash: ${source.source_content_hash || 'No source hash recorded'}`,
    `Ruleset hash: ${attempt?.rule_manifest_hash || payload.law_view?.rule_manifest_hash || 'No ruleset hash recorded'}`,
    `Result link: ${origin}${path || '/review'}`,
    '', 'Feedback:', note.trim(),
  ].join('\n');
}
